import { useContext, useEffect, useMemo, useState } from 'react';

import { selectAccessibilityPrefs } from '../../../../../db/app/selectors';
import { fetchComments } from '../../../../../db/comments/actions';
import {
  selectCommentContext,
  selectComments,
  selectLastPageFirstCommentId,
  selectTotalComments,
} from '../../../../../db/comments/selectors';
import { useAppSelector } from '../../../../../store/hooks';
import { useRequestDispatch } from '../../../../../utils/request-actions';
import { CommentsContext } from '../../../../Context';
import { getCommentElId, getCommentTextElId } from '../../../../helpers';

const useLoadOlderCommentsVM = () => {
  const requestDispatch = useRequestDispatch();

  const { turnOff } = useAppSelector((state) => selectAccessibilityPrefs(state));
  const isScreenReaderOn = !turnOff;

  const context = useAppSelector((state) => selectCommentContext(state));
  const comments = useAppSelector((state) => selectComments(state));
  const totalComments = useAppSelector((state) => selectTotalComments(state));

  const lastPageFirstCommentId = useAppSelector((state) => selectLastPageFirstCommentId(state));

  const hasMoreComments = useMemo(() => comments.length < totalComments, [comments.length, totalComments]);

  const [loadedOlderComments, setLoadedOlderComments] = useState(false);
  const [announcementText, setAnnouncementText] = useState('');

  const { pageNumber, incrementPageNumber } = useContext(CommentsContext);

  const handleLoadOlderComments = async () => {
    if (!context || !comments.length) return;

    await requestDispatch(fetchComments, {
      context,
      lastCommentId: comments[0]?.id,
    });

    setLoadedOlderComments(true);

    incrementPageNumber();
  };

  useEffect(
    function onCommentsCountChanged() {
      if (!isScreenReaderOn) return;
      setAnnouncementText(`more comments loaded, ${comments.length} total comments`);
    },
    [comments.length, isScreenReaderOn]
  );

  useEffect(
    function onLoadedOlderComments() {
      if (!loadedOlderComments) return;

      if (!lastPageFirstCommentId) return;

      const commentElId = isScreenReaderOn
        ? getCommentTextElId(lastPageFirstCommentId)
        : getCommentElId(lastPageFirstCommentId);

      const commentRichTextEl = document.getElementById(commentElId);
      if (!commentRichTextEl) return;

      commentRichTextEl.focus();
    },
    [lastPageFirstCommentId, loadedOlderComments, isScreenReaderOn]
  );

  return { isScreenReaderOn, hasMoreComments, pageNumber, handleLoadOlderComments, announcementText };
};

export default useLoadOlderCommentsVM;
